import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewOrderContext } from "../../../contexts/ViewOrderContext";
import ToolTip from "../../../components/component-tools/ToolTip";
import { formatDate } from "../../../services/helpers";
import SendFinalReport from "../../../components/order-components/SendFinalReport";
import ConfirmDelete from "../../../components/component-tools/ConfirmDelete";
import { deleteCriminalOrder } from "../../../services/criminalOrderService";

interface CriminalOrderSectionProps {
  handleInputChange: (e: any, field: string, type?: string) => void;
  getServiceNames: (type: number) => string[];
}

const CriminalOrderSection: React.FC<CriminalOrderSectionProps> = ({
  handleInputChange,
  getServiceNames,
}) => {
  const navigate = useNavigate();
  const [showCriminalFinalReportPopup, setShowCriminalFinalReportPopup] =
    useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  const {
    order,
    criminalOrder,
    setCriminalOrder,
    criminalDetails,
    setCriminalDetails,
    services,
    vendors,
    client,
    criminalFinalUrl,
    canSendCriminalFinal,
  } = useViewOrderContext();

  if (!criminalOrder || criminalOrder.criminalOrderID === 0) return null;

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    setLoadingSpinner(true);

    try {
      await deleteCriminalOrder(criminalOrder.criminalOrderID);
      setCriminalOrder(undefined);
      setCriminalDetails([]);
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="data-container">
      <div className="text-container-header" style={{ flex: "0 0 30%" }}>
        Criminal/Public Record Services
      </div>
      <div
        style={{
          flex: "0 0 65%",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: "20px" }}>
          <div className="field-long" style={{ flex: "0 0 20%" }}>
            <button
              className={`oxbtn btn-new ${
                order.orderStatusID === 5 || order.orderStatusID === 4
                  ? "disabled"
                  : ""
              }`}
              disabled={order.orderStatusID === 5 || order.orderStatusID === 4}
              onClick={() => {
                localStorage.setItem("scrollY", window.scrollY.toString());
                navigate("/admin/order-service", {
                  state: { criminalOrderID: criminalOrder.criminalOrderID },
                });
              }}
            >
              <div> Order Service</div>
              <i className="bi bi-plus-circle-fill plus-icon"></i>
            </button>
          </div>
          <div className="field-long" style={{ flex: "0 0 15%" }}>
            <label className="field-label">Years:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={criminalOrder.years}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="checkbox-long">
            <div className="field-input" style={{ flex: "0 0 3%" }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  criminalOrder.adminReviewed
                    ? criminalOrder.adminReviewed
                    : false
                }
                onChange={(e) =>
                  handleInputChange(e, "adminReviewed", "criminal")
                }
                disabled={order.orderStatusID === 5}
              />
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <label className="field-label">Admin Reviewed </label>
              <ToolTip
                tooltipText="Please check this box if admin has completed all action items on this order. i.e. entered all vendor orders."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
            <div></div>
          </div>
          <div className="checkbox-long">
            <div className="field-input" style={{ flex: "0 0 3%" }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  criminalOrder.inAdminReview
                    ? criminalOrder.inAdminReview
                    : false
                }
                onChange={(e) =>
                  handleInputChange(e, "inAdminReview", "criminal")
                }
                disabled={order.orderStatusID === 5}
              />
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <label className="field-label">In Review</label>
              <ToolTip
                tooltipText="Please check this box if an admin is currently working on this piece of the order."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
            <div></div>
          </div>
        </div>
        {confirmDeleteOpen && (
          <ConfirmDelete
            onConfirm={() => handleDelete()}
            onCancel={() => {
              setConfirmDeleteOpen(false);
            }}
            message="Delete Criminal Order? All details will be deleted."
          />
        )}

        {!criminalDetails.some((cd) => cd.initiatedDate) && (
          <div className="close-button-container">
            <button
              className="delete-button"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
      </div>
      <div style={{ flex: "0 0 100%" }}>
        <ul>
          {getServiceNames(1).map((serviceName, index) => (
            <li key={index}>{serviceName}</li>
          ))}
        </ul>
      </div>
      {criminalOrder.convictedOfCrime && (
        <div className="error">CANDIDATE HAS ADMITTED TO A CRIME</div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        <div className="field-long">
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Invoice Date:</label>
            <input
              className="form-control"
              type="date"
              value={formatDate(criminalOrder.invoiceDate)}
              disabled
            />
          </div>
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Billed Date:</label>
            <input
              className="form-control"
              type="date"
              value={formatDate(criminalOrder.billedDate)}
              disabled
            />
          </div>
        </div>
        <div className="field-long" style={{ alignItems: "end" }}>
          {criminalOrder.finalReportUrl && (
            <>
              <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                <label className="field-label">Final Report Date:</label>
                <input
                  className="form-control"
                  type="date"
                  value={formatDate(criminalOrder.finalReportDate)}
                  disabled
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "end",
                }}
              >
                <button
                  className="oxbtn btn-purple"
                  onClick={() => {
                    if (criminalFinalUrl) {
                      window.open(criminalFinalUrl, "_blank");
                    }
                  }}
                >
                  View Final Report
                  <i className="btn-icon bi bi-box-arrow-up-right"></i>
                </button>
              </div>
            </>
          )}

          <div>
            <button
              className={`oxbtn btn-grey ${
                order.orderStatusID === 5 || !canSendCriminalFinal
                  ? "disabled"
                  : ""
              }`}
              onClick={() => setShowCriminalFinalReportPopup(true)}
              disabled={order.orderStatusID === 5 || !canSendCriminalFinal}
            >
              Send Final Report
              <i className="btn-icon bi bi-send"></i>
            </button>

            {showCriminalFinalReportPopup && client && (
              <SendFinalReport
                client={client}
                order={order}
                criminalOrder={criminalOrder}
                criminalDetails={criminalDetails}
                onClose={() => {
                  setShowCriminalFinalReportPopup(false);
                  window.location.reload();
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <div className="table-container">
          <table className="table" style={{ cursor: "pointer" }}>
            <thead>
              <tr>
                <th style={{ width: "20px" }}></th>
                <th style={{ width: "180px" }}>Service</th>
                <th style={{ width: "130px" }}></th>
                <th style={{ width: "130px" }}></th>
                <th style={{ width: "120px" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <div>Initiated</div>
                  </div>
                </th>
                <th style={{ width: "120px" }}>Received</th>
                <th style={{ width: "150px" }}>Vendor</th>
                <th style={{ width: "80px" }}>Red Flag</th>
                <th style={{ width: "80px" }}>Fee</th>
              </tr>
            </thead>
            <tbody>
              {criminalDetails
                ?.filter((detail) => !detail.subscriptionAddOn)
                .map((detail) => {
                  const service = services.find(
                    (s) => s.serviceID === detail.serviceID,
                  );
                  return (
                    <tr
                      key={detail.criminalDetailID}
                      onClick={() => {
                        localStorage.setItem(
                          "scrollY",
                          window.scrollY.toString(),
                        );
                        navigate(
                          `/admin/order-service/${detail.criminalDetailID}`,
                          {
                            state: {
                              criminalOrderID: criminalOrder.criminalOrderID,
                              criminalDetailID: detail.criminalDetailID,
                              closed:
                                criminalOrder.finalReportUrl !== null
                                  ? true
                                  : false,
                            },
                          },
                        );
                      }}
                      className={detail.inActive ? "inactive" : ""}
                    >
                      <td>
                        {detail.hasResponse && (
                          <i className="bi bi-paperclip attachment-icon"></i>
                        )}
                      </td>
                      <td>
                        {service
                          ? service.serviceName
                          : `Unknown Service (ID: ${detail.serviceID})`}
                      </td>
                      <td>{detail.resultsFieldOne}</td>
                      <td>{detail.resultsFieldTwo}</td>
                      <td>{formatDate(detail.initiatedDate, "us")}</td>
                      <td>{formatDate(detail.receivedDate, "us")}</td>
                      <td>
                        {vendors.find(
                          (vendor) => vendor.vendorID === detail.vendorID,
                        )?.vendorName || ``}
                      </td>

                      <td
                        style={{
                          color: detail.redFlag ? "red" : "inherit",
                          fontWeight: detail.redFlag ? "bold" : "normal",
                        }}
                      >
                        {detail.redFlag ? "Red Flag" : ""}
                      </td>
                      <td>{detail.fee}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
      {criminalDetails?.some((cr) => cr.subscriptionAddOn) && (
        <div style={{ width: "100%" }}>
          <div className="text-container-header" style={{ flex: "0 0 30%" }}>
            Subscription Add-Ons
          </div>
          <div className="table-wrapper">
            <div className="table-container">
              <table className="table" style={{ cursor: "pointer" }}>
                <thead>
                  <tr>
                    <th style={{ width: "20px" }}></th>
                    <th style={{ width: "120px" }}>Service</th>
                    <th style={{ width: "130px" }}></th>
                    <th style={{ width: "130px" }}></th>
                    <th style={{ width: "120px" }}>
                      <div style={{ display: "flex", gap: "5px" }}>
                        <div>Initiated</div>
                      </div>
                    </th>
                    <th style={{ width: "120px" }}>Received</th>
                    <th style={{ width: "150px" }}>Vendor</th>
                    <th style={{ width: "80px" }}>Red Flag</th>
                    <th style={{ width: "120px" }}>Client Notified</th>
                    <th style={{ width: "140px" }}>Admin Reviewed</th>
                  </tr>
                </thead>
                <tbody>
                  {criminalDetails
                    ?.filter((detail) => detail.subscriptionAddOn)
                    .map((detail) => {
                      const service = services.find(
                        (s) => s.serviceID === detail.serviceID,
                      );
                      return (
                        <tr
                          key={detail.criminalDetailID}
                          onClick={() => {
                            localStorage.setItem(
                              "scrollY",
                              window.scrollY.toString(),
                            );
                            navigate(
                              `/admin/order-service/${detail.criminalDetailID}`,
                              {
                                state: {
                                  criminalOrderID:
                                    criminalOrder.criminalOrderID,
                                  criminalDetailID: detail.criminalDetailID,
                                  closed:
                                    criminalOrder.finalReportUrl !== null
                                      ? true
                                      : false,
                                },
                              },
                            );
                          }}
                          className={detail.inActive ? "inactive" : ""}
                        >
                          <td>
                            {detail.hasResponse && (
                              <i className="bi bi-paperclip attachment-icon"></i>
                            )}
                          </td>
                          <td>
                            {service
                              ? service.serviceName
                              : `Unknown Service (ID: ${detail.serviceID})`}
                          </td>
                          <td>{detail.resultsFieldOne}</td>
                          <td>{detail.resultsFieldTwo}</td>
                          <td>{formatDate(detail.initiatedDate, "us")}</td>
                          <td>{formatDate(detail.receivedDate, "us")}</td>
                          <td>
                            {vendors.find(
                              (vendor) => vendor.vendorID === detail.vendorID,
                            )?.vendorName || ``}
                          </td>
                          <td
                            style={{
                              color: detail.redFlag ? "red" : "inherit",
                              fontWeight: detail.redFlag ? "bold" : "normal",
                            }}
                          >
                            {detail.redFlag ? "Red Flag" : ""}
                          </td>
                          <td>{formatDate(detail.clientNotifiedDate, "us")}</td>
                          <td>{detail.adminReviewed ? "Yes" : ""}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CriminalOrderSection;
